import Vue from 'vue';

import { getResources } from '../client/resource';
import { FrameworkResource } from '../../../common/framework/enumeration/FrameworkResource';
import {
    camelToHyphenated,
    underscoreToCamel,
    underscoreToHyphenated,
    upperCamelToLowerCamel,
} from '../../../common/framework/util/convert';
import { Cache, cachedLoad, ValueLoadedToCacheCallback } from './cache_service';
import { Option } from '../../../common/framework/model/Option';
import { sortAlphabetically } from '../../application/service/helper_service';

export interface OptionValue {
    id: string | undefined;
    label: string;
}

const optionsCache: Cache<OptionValue[]> = {
    values: new Map<string, OptionValue[]>(),
    callbacks: new Map<string, Array<ValueLoadedToCacheCallback<OptionValue[]>>>(),
};

export async function getResourceOptions(resource: string, field: string) {
    return (
        await cachedLoad(
            resource + '-options',
            async () => {
                return [{ id: undefined as string | undefined, label: '-' }].concat(
                    (await getResources<any>(resource, -1)).map((o) => {
                        return { id: o.id, label: o[field] };
                    }),
                );
            },
            optionsCache,
        )
    ).sort(sortAlphabetically('label'));
}

export async function getGroupOptions() {
    return getResourceOptions('group', 'name');
}

/**
 * Lazily built id -> label indexes, keyed by the options array itself.
 *
 * Called once per label cell per row, against whole resource lists - customers and vendors are
 * loaded with pageSize 2000 - so even a single find() per call is O(rows x options) per render on
 * the wider grids. The index is keyed by array identity so it is collected along with the array,
 * and the cached size catches the arrays that get mutated in place after loading.
 */
const optionLabelIndexes = new WeakMap<object, { size: number; labels: Map<unknown, string> }>();

function getOptionLabelIndex(options: Array<{ id: string | undefined; label: string }>) {
    const cached = optionLabelIndexes.get(options);

    if (cached && cached.size === options.length) {
        return cached.labels;
    }

    const labels = new Map<unknown, string>();

    // First match wins, as find() did.
    for (const option of options) {
        if (!labels.has(option.id)) {
            labels.set(option.id, option.label);
        }
    }

    optionLabelIndexes.set(options, { size: options.length, labels });

    return labels;
}

export function getOptionLabel(id: string, options: Array<{ id: string | undefined; label: string }>) {
    const label = getOptionLabelIndex(options).get(id);

    return label === undefined ? id : label;
}

export function getEnumOptions(enumName: string, enumKeys: string[]) {
    return [{ id: undefined as string | undefined, labelKey: '' }].concat(
        enumKeys.map((key) => {
            return {
                id: underscoreToHyphenated(key),
                labelKey: 'enum.' + upperCamelToLowerCamel(enumName) + '.' + underscoreToCamel(key.toLowerCase()),
            };
        }),
    );
}

export function getEnumerationOptions(enumName: string, keys: string[], component: Vue) {
    return getEnumOptions(enumName, keys)
        .map((o) => {
            const localizedValue = (component as any).$t(o.labelKey).toString();
            if (localizedValue === o.labelKey && o.labelKey !== '') {
                if (typeof sessionStorage !== 'undefined') {
                    console.log('writing missing localization key to session storage: ' + o.labelKey);
                    if (!sessionStorage.getItem('missingLocalizationKeys')) {
                        sessionStorage.setItem('missingLocalizationKeys', JSON.stringify({}));
                    }
                    const missingLocalizationKeys = JSON.parse(sessionStorage.getItem('missingLocalizationKeys')!!);
                    const fields = o.labelKey.split('.');
                    if (fields.length !== 0 && fields[0] !== '') {
                        let obj = missingLocalizationKeys;
                        for (let i = 0; i < fields.length; i++) {
                            const field = fields[i];
                            const last = i === fields.length - 1;
                            if (typeof obj[field] === 'undefined') {
                                if (!last) {
                                    obj[field] = {};
                                } else {
                                    obj[field] = '';
                                }
                            }
                            obj = obj[field]!!;
                        }
                        sessionStorage.setItem(
                            'missingLocalizationKeys',
                            JSON.stringify(missingLocalizationKeys, null, 2),
                        );
                    }
                }
            }
            return { id: o.id, label: localizedValue };
        })
        .sort(sortAlphabetically('label'));
}

export async function getDatabaseOptions(typeName: string, component: Vue) {
    const type = camelToHyphenated(typeName);
    return (
        await cachedLoad(
            type + '-database-options',
            async () => {
                console.log('loading database options: ' + type);
                const parameters = new Map();
                parameters.set('type', type);
                parameters.set('sortField', 'index');
                parameters.set('sortOrder', 'asc');
                const options = await getResources<Option>(FrameworkResource.OPTION, -1, parameters);
                return [{ id: undefined as string | undefined, label: '-' }].concat(
                    options
                        .sort((a, b) => a.index - b.index)
                        .map((o) => {
                            const data = o.metadata as any;
                            return { id: o.key, label: (component as any).$i18n.locale === 'fi' ? data.labelFi : data.labelEn };
                        }),
                );
            },
            optionsCache,
        )
    ).sort(sortAlphabetically('label'));
}
